import React from 'react';

import { Box, Button, H1, H2, H3, HStack, ShimmerText, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';

import AiOrb from './assets/icon_ai_orb.svg';
import { AccountHighlight } from './components/AccountHighlight';
import { OpenAccountButton } from './components/OpenAccountButton';
import { RoundedBorderBox } from './components/RoundedBorderBox';
import { AccountRecommendationOptionQuery } from './graphql/query.accountRecommendationOption.generated';
import { useAccountRecommendation } from './hooks/useAccountRecommendation';

import { urlBuilder } from '../../Shared/urlBuilder';

type AccountRecommendationOption = NonNullable<AccountRecommendationOptionQuery['accountRecommendationOption']>;

type Props = { initialRecommendationOption: AccountRecommendationOption };
export const RecommendationContent: React.FC<Props> = ({ initialRecommendationOption }) => {
  const {
    currentRecommendation,
    isCreationLoading,
    isSkipLoading,
    createAccountRecommendation,
    skipAccountRecommendation,
  } = useAccountRecommendation({ initialRecommendationOption });
  const { option } = currentRecommendation;
  const reasonsToBelieve = useFallbackKeys(option.reasonsToBelieve);

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack css={{ gap: 'space.100' }}>
        <H1 variant="brandTitle" color="text" css={{ fontSize: 36 }}>
          Recommended for you
        </H1>
        <Text variant="text100" color="text.weak">
          Based on your financial profile and connected accounts.{' '}
          <SambaAutoLink to={urlBuilder.appFinancialProfileEdit.absolute()}>Review profile</SambaAutoLink>
        </Text>
      </VStack>
      <HStack
        css={{
          background: 'background.neutral',
          borderRadius: 'large',
          overflow: 'hidden',
          alignItems: 'stretch',
          minHeight: 'fit-content',
        }}
      >
        <Box css={{ flex: 1, minWidth: 0, display: 'flex' }}>
          <ThemedImage
            themedAsset={option.image}
            alt={option.name}
            css={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        <VStack css={{ flex: 4, padding: 'space.300', gap: 'space.100' }}>
          <H2 variant="title200" color="text">
            {option.name}
          </H2>
          <VStack css={{ gap: 'space.50' }}>
            {reasonsToBelieve.map(({ item, key }) => (
              <AccountHighlight key={key} text={item} />
            ))}
          </VStack>
        </VStack>
      </HStack>
      {currentRecommendation.reasoning || isCreationLoading ? (
        <VStack css={{ gap: 'space.200', width: '100%' }}>
          <RoundedBorderBox borderStyle="gradient" style={{ width: '100%' }}>
            <VStack css={{ padding: 'space.200', gap: 'space.200', width: '100%' }}>
              {currentRecommendation.reasoning ? (
                <>
                  <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
                    <img src={AiOrb} alt="AI Icon" width="20px" height="20px" />
                    <H3 variant="title100" color="text">
                      Why this makes sense for you
                    </H3>
                  </HStack>
                  <Text
                    variant="text50"
                    color="text"
                    dangerouslySetInnerHTML={{ __html: currentRecommendation.reasoning }}
                  />
                  <Text variant="text25" color="text.weak">
                    AI-summary based on your financial profile.{' '}
                    <SambaAutoLink to="/foo" target="_blank">
                      Learn more
                    </SambaAutoLink>
                  </Text>
                </>
              ) : (
                <>
                  <ShimmerText textStyle="text25" />
                  <ShimmerText textStyle="text25" />
                  <Box css={{ width: '66%' }}>
                    <ShimmerText textStyle="text25" />
                  </Box>
                </>
              )}
            </VStack>
          </RoundedBorderBox>
        </VStack>
      ) : (
        <VStack css={{ gap: 'space.200' }}>
          <RoundedBorderBox borderStyle="gradient" borderRadius={20} style={{ width: '100%' }}>
            <Button
              variant="textSecondary"
              css={{ borderRadius: '20px', textDecoration: 'none', background: 'none', width: '102%', padding: '1px' }}
              onClick={() => createAccountRecommendation()}
            >
              <HStack
                css={{
                  px: 'space.150',
                  py: 'space.100',
                  borderRadius: '20px',
                  background: 'background.surface',
                  gap: 'space.50',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={AiOrb} alt="AI Icon" />
                <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
                  Why recommend this account?
                </Text>
              </HStack>
            </Button>
          </RoundedBorderBox>
          <Text variant="text25">
            Generates an AI summary based on your financial profile. We never use your data to train our models.{' '}
            <SambaAutoLink to="/foo" target="_blank">
              Learn more
            </SambaAutoLink>
          </Text>
        </VStack>
      )}
      <HStack css={{ paddingTop: 'space.100', gap: 'space.100', alignItems: 'center' }}>
        <OpenAccountButton option={option} isEnabled={!isSkipLoading} />
        {currentRecommendation.isSkipEligible && (
          <Button variant="ghostPrimary" loading={isSkipLoading} onClick={() => skipAccountRecommendation()}>
            Next recommendation
          </Button>
        )}
      </HStack>
    </VStack>
  );
};
